/**
 * SeizureSkillEffect — Lightning tree Pulse path evolution (replaces Pulse).
 *
 * A full-second EM sentence. Where Pulse disrupted an enemy briefly, Seizure
 * scrambles their entire nervous system — motor control gone, decisions gone,
 * leaving them to twitch helplessly while the player unloads. Disrupted enemies
 * under Seizure take 20% more damage from all sources (the `seizure_vulnerability`
 * debuff), stacking additively with Voltage (25%) and Wired (15% Lightning-only).
 *
 * Mechanically identical to Pulse with three upgrades:
 *   1. Base disruption raised from 800ms → 1000ms.
 *   2. Applies `seizure_vulnerability` alongside disruption — a debuff that
 *      CombatHitResolver multiplies against incoming damage while active.
 *      Duration mirrors the disruption duration so they expire together.
 *   3. `skillId` is 'seizure', but all resolver lookups use the 'pulse' scope
 *      so Lockdown's +500ms and Resonant Pulse's residue keys inherit cleanly.
 *
 * On activation:
 *   1. rollSkillSuccess('seizure') — fizzle at low attunement XP.
 *   2. Resolve range: baseRangeTiles × getMultiplicativeEffect('pulse', 'rangeMultiplier').
 *   3. Resolve disruption duration: 1000ms + getAdditiveEffect('pulse', 'disruptDurationMs').
 *   4. Fire EM burst via aoe.hit().
 *   5. On hit: consume Static charge, interrupt enemy, apply brief 200ms stun
 *      (immunity-gated), apply disruption (sourceSkillId: 'seizure'), apply
 *      `seizure_vulnerability` for the same duration, spawn em_residue zone
 *      if Resonant Pulse is unlocked.
 *
 * Upgrade key inheritance (resolver scope: 'pulse'):
 * - disruptDurationMs: additive — added to 1000ms base.
 * - rangeMultiplier: multiplicative — scales 1.5-tile reach.
 * - residueRadiusTiles: direct — Resonant Pulse zone radius (1 tile).
 * - residueDurationMs: direct — Resonant Pulse zone lifetime (1500ms).
 * - residueDisruptMs: direct — Resonant Pulse disruption tick duration (300ms).
 *
 * Damage vulnerability stacking on a disrupted enemy with all three unlocked:
 *   Voltage (25% all-damage) + Seizure vulnerability (20% all-damage) + Wired (15% Lightning)
 *   = 1.45× all-damage, 1.60× Lightning — additive within category, not compounded.
 *
 * @see ADR-064 (attunement shared systems architecture)
 * @see ADR-065 (CC hierarchy — disruption has no immunity window)
 * @see lightning-spec.md § Seizure (`seizure`)
 * @see lightning-spec.md § Discrepancy Log (no disruptDurationMs in JSON → 1000ms from spec)
 */

import type { SkillEffect } from '../../SkillEffect.js';
import type { PulseHitResult, PulseZoneConfig } from './PulseSkillEffect.js';

// ---------------------------------------------------------------------------
// Re-export shared types so callers can import from one place
// ---------------------------------------------------------------------------

export type { PulseHitResult, PulseZoneConfig };

// ---------------------------------------------------------------------------
// Dependency interfaces
// ---------------------------------------------------------------------------

export interface SeizureAoEConfig {
  /** Reach from cursor/player in tiles. */
  readonly rangeTiles: number;
  /** Burst radius in tiles. */
  readonly radiusTiles: number;
  readonly damageMultiplier: number;
  readonly sourceSkillId: string;
}

export interface SeizureDeps {
  readonly resolver: {
    getAdditiveEffect(skillId: string, key: string): number;
    getMultiplicativeEffect(skillId: string, key: string): number;
  };
  readonly aoe: {
    /** Fire the EM burst. Returns hit count and impact world position. */
    hit(config: SeizureAoEConfig): PulseHitResult;
  };
  readonly statusEffects: {
    apply(defId: string, opts: { sourceSkillId: string; durationMs: number }): void;
    /**
     * True if the target is immune to the given status. Disruption and
     * seizure_vulnerability have no immunity window — only stun is checked.
     */
    isImmune(defId: string): boolean;
  };
  readonly charge: {
    /** Consume all Static charge. Returns amount consumed (0 if empty). */
    consume(chargeId: string): number;
  };
  /**
   * Called for each hit enemy to cancel any in-progress attack animation,
   * returning the enemy to idle. Maps to enemyAI.interruptCurrentAction().
   */
  readonly interruptEnemy?: () => void;
  readonly rollSkillSuccess: (skillId: string) => boolean;
  readonly breakHide?: () => void;
  /**
   * Zone system interface. Required only when Resonant Pulse (pulse_field)
   * is unlocked — inherited via resolver scope 'pulse'.
   */
  readonly zones?: {
    create(config: PulseZoneConfig): void;
  };
}

// ---------------------------------------------------------------------------
// Config
// ---------------------------------------------------------------------------

export interface SeizureConfig {
  /** Medium cooldown tier: 800ms. */
  readonly cooldownMs: number;
  /**
   * Base disruption duration in ms. Spec: 1000ms ("Full-second disruption").
   * JSON node has no explicit disruptDurationMs — inferred from description text
   * per Discrepancy Log in lightning-spec.md.
   */
  readonly baseDisruptDurationMs: number;
  /** Base AoE reach in tiles. Spec: 1.5 tiles. */
  readonly baseRangeTiles: number;
  /** AoE burst radius in tiles. Spec: 0.5 tiles. */
  readonly baseRadiusTiles: number;
  /** Brief stun duration in ms. Spec: 200ms (disruption is the primary CC). */
  readonly baseStunDurationMs: number;
}

const DEFAULT_CONFIG: SeizureConfig = {
  cooldownMs: 800,
  baseDisruptDurationMs: 1000,
  baseRangeTiles: 1.5,
  baseRadiusTiles: 0.5,
  baseStunDurationMs: 200,
};

// ---------------------------------------------------------------------------
// Skill effect
// ---------------------------------------------------------------------------

export class SeizureSkillEffect implements SkillEffect {
  readonly skillId = 'seizure';
  readonly type = 'active' as const;
  readonly activationMode = 'instant' as const;
  readonly missLabel = 'MISS';
  readonly failLabel = 'FIZZLE';

  private readonly deps: SeizureDeps;
  private readonly config: SeizureConfig;

  private _cooldownRemaining = 0;
  private _activations = 0;
  private _hitsLanded = 0;
  private _stunApplications = 0;
  private _disruptApplications = 0;
  private _vulnerabilityApplications = 0;
  private _interrupts = 0;
  private _fizzles = 0;
  private _totalChargeConsumed = 0;
  private _resolvedRangeTiles = 0;
  private _resolvedDisruptDurationMs = 0;
  private _zoneSpawns = 0;

  constructor(deps: SeizureDeps, config: Partial<SeizureConfig> = {}) {
    this.deps = deps;
    this.config = { ...DEFAULT_CONFIG, ...config };
  }

  // --- State getters (for simulation assertions) ---

  get cooldown(): number { return this._cooldownRemaining; }
  get activations(): number { return this._activations; }
  get hitsLanded(): number { return this._hitsLanded; }
  get stunApplications(): number { return this._stunApplications; }
  get disruptApplications(): number { return this._disruptApplications; }
  get vulnerabilityApplications(): number { return this._vulnerabilityApplications; }
  get interrupts(): number { return this._interrupts; }
  get fizzles(): number { return this._fizzles; }
  get totalChargeConsumed(): number { return this._totalChargeConsumed; }
  get resolvedRangeTiles(): number { return this._resolvedRangeTiles; }
  get resolvedDisruptDurationMs(): number { return this._resolvedDisruptDurationMs; }
  get zoneSpawns(): number { return this._zoneSpawns; }

  // --- SkillEffect interface ---

  canActivate(): boolean {
    return this._cooldownRemaining <= 0;
  }

  activate(): boolean {
    if (!this.canActivate()) return false;

    // Fizzle check — low attunement XP produces a visible spark but no effect.
    if (!this.deps.rollSkillSuccess('seizure')) {
      this._fizzles++;
      return false;
    }

    // Break Hide on activation.
    this.deps.breakHide?.();

    // Resolve upgrade-modified stats (resolver scope: 'pulse' — Seizure uses
    // replacesId: "pulse" so all Pulse upgrade keys route here).
    this._resolvedRangeTiles =
      this.config.baseRangeTiles *
      this.deps.resolver.getMultiplicativeEffect('pulse', 'rangeMultiplier');

    this._resolvedDisruptDurationMs =
      this.config.baseDisruptDurationMs +
      this.deps.resolver.getAdditiveEffect('pulse', 'disruptDurationMs');

    // Fire the EM burst.
    const hitResult = this.deps.aoe.hit({
      rangeTiles: this._resolvedRangeTiles,
      radiusTiles: this.config.baseRadiusTiles,
      damageMultiplier: 1.0,
      sourceSkillId: 'seizure',
    });

    this._activations++;

    if (hitResult.enemiesHit > 0) {
      this._hitsLanded++;

      // Consume Static charge — bonus damage applied to the burst.
      const consumed = this.deps.charge.consume('static_charge');
      this._totalChargeConsumed += consumed;

      // Cancel any in-progress attack or ability on the hit enemy.
      this.deps.interruptEnemy?.();
      this._interrupts++;

      // Brief stun — immunity-gated (stun has a 300ms immunity window).
      if (!this.deps.statusEffects.isImmune('stun')) {
        this.deps.statusEffects.apply('stun', {
          sourceSkillId: 'seizure',
          durationMs: this.config.baseStunDurationMs,
        });
        this._stunApplications++;
      }

      // Disruption — no immunity window; apply unconditionally.
      // sourceSkillId: 'seizure' distinguishes Seizure's disruption from
      // other sources — CombatHitResolver uses this to verify the vulnerability
      // was set by Seizure specifically.
      // STUB: deferred — no enemy ability/cast system reads disruption
      this.deps.statusEffects.apply('disruption', {
        sourceSkillId: 'seizure',
        durationMs: this._resolvedDisruptDurationMs,
      });
      this._disruptApplications++;

      // Seizure vulnerability — 20% all-damage bonus while disruption is active.
      // Applied for the same duration as disruption so they expire together.
      // CombatHitResolver checks for this status and applies a ×1.20 multiplier
      // (additive alongside Voltage's ×1.25 and Wired's ×1.15 Lightning-only bonus).
      this.deps.statusEffects.apply('seizure_vulnerability', {
        sourceSkillId: 'seizure',
        durationMs: this._resolvedDisruptDurationMs,
      });
      this._vulnerabilityApplications++;

      // Resonant Pulse (pulse_field): spawn em_residue zone at impact point.
      // Keys read from 'pulse' resolver scope — inherited into Seizure.
      const residueRadiusTiles = this.deps.resolver.getAdditiveEffect('pulse', 'residueRadiusTiles');
      if (residueRadiusTiles > 0 && this.deps.zones) {
        const residueDurationMs = this.deps.resolver.getAdditiveEffect('pulse', 'residueDurationMs');
        const residueDisruptMs = this.deps.resolver.getAdditiveEffect('pulse', 'residueDisruptMs');
        this.deps.zones.create({
          type: 'em_residue',
          x: hitResult.hitX,
          y: hitResult.hitY,
          radiusTiles: residueRadiusTiles,
          durationMs: residueDurationMs > 0 ? residueDurationMs : 1500,
          tickIntervalMs: 200,
          dotDefId: 'disruption',
          dotDurationMs: residueDisruptMs > 0 ? residueDisruptMs : 300,
          sourceSkillId: 'seizure',
        });
        this._zoneSpawns++;
      }
    }

    // Start cooldown.
    this._cooldownRemaining = this.config.cooldownMs;

    return true;
  }

  deactivate(): void {
    // Instant — no toggle state.
  }

  isActive(): boolean {
    return false;
  }

  update(delta: number): void {
    if (this._cooldownRemaining > 0) {
      this._cooldownRemaining = Math.max(0, this._cooldownRemaining - delta);
    }
  }

  destroy(): void {
    this._cooldownRemaining = 0;
  }
}
